import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X, ExternalLink } from 'lucide-react';

import Copy from '@/components/Copy';
import { formatLamportsToSol } from '@/utils/solana/lamportsToSol';
import {
  getTransactionExplorerUrl,
  truncateSignature,
  type SolanaCluster,
} from '@/utils/solana/explorerUrl';
import config from '@/config';

interface TransactionSuccessModalProps {
  type: 'deposit' | 'withdraw';
  signature: string;
  amount: bigint;
  onClose: () => void;
}

export function TransactionSuccessModal({
  type,
  signature,
  amount,
  onClose,
}: TransactionSuccessModalProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Determine network cluster from config
  const isMainnet = config.SOLANA_NETWORK?.id === 'solana:mainnet';
  const cluster: SolanaCluster = isMainnet ? 'mainnet-beta' : 'devnet';

  const explorerUrl = getTransactionExplorerUrl(signature, cluster);
  const truncatedSignature = truncateSignature(signature, 8, 8);
  const amountSol = formatLamportsToSol(amount);

  // Color scheme based on transaction type
  const primaryColor = type === 'deposit' ? '#FEBB01' : '#50E5F8';
  const title = type === 'deposit' ? 'DEPOSIT SUCCESSFUL' : 'WITHDRAWAL SUCCESSFUL';
  const message =
    type === 'deposit'
      ? 'Your SOL has been deposited to your on-chain account!'
      : 'Your SOL has been withdrawn to your wallet!';

  if (!isMounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-xl shadow-2xl transform transition-all animate-in fade-in zoom-in duration-300"
        style={{
          backgroundColor: '#343434',
          border: `2px solid ${primaryColor}`,
          boxShadow: `0 0 30px ${primaryColor}40`,
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6" style={{ borderBottom: `2px solid ${primaryColor}` }}>
          <div className="flex items-center space-x-3">
            <CheckCircle2
              className="w-6 h-6 animate-in zoom-in duration-500"
              style={{ color: primaryColor }}
            />
            <h3
              className="text-lg font-semibold"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
            >
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'white' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Success Message */}
          <div className="text-center">
            <div
              className="text-sm mb-2"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.8)' }}
            >
              {message}
            </div>
          </div>

          {/* Amount Display */}
          <div
            className="rounded-lg p-4 text-center"
            style={{
              backgroundColor: `${primaryColor}15`,
              border: `2px solid ${primaryColor}`,
            }}
          >
            <div
              className="text-sm mb-1"
              style={{ fontFamily: 'Outfit, sans-serif', color: primaryColor }}
            >
              Amount {type === 'deposit' ? 'Deposited' : 'Withdrawn'}
            </div>
            <div
              className="text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}
            >
              {amountSol}{' '}
              <span style={{ fontFamily: 'Hyperspace Race Capsule, cursive', fontSize: '1.5rem' }}>
                SOL
              </span>
            </div>
          </div>

          {/* Transaction Signature */}
          <div
            className="rounded-lg p-4"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
          >
            <div
              className="text-xs mb-2"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.6)' }}
            >
              Transaction Signature
            </div>
            <div className="flex items-center justify-between space-x-2">
              <code
                className="text-sm flex-1 overflow-hidden"
                style={{ fontFamily: 'monospace', color: 'white' }}
              >
                {truncatedSignature}
              </code>
              <Copy text={signature} className="h-5 w-5 flex-shrink-0" />
            </div>
          </div>

          {/* Network Badge */}
          <div className="flex justify-center">
            <div
              className="text-xs px-3 py-1 rounded-full"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: primaryColor,
                backgroundColor: `${primaryColor}20`,
                border: `1px solid ${primaryColor}40`,
              }}
            >
              {cluster === 'mainnet-beta' ? 'Mainnet' : 'Devnet'}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex space-x-3 p-6" style={{ borderTop: `2px solid ${primaryColor}` }}>
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 rounded-lg transition-colors"
            style={{
              border: `2px solid ${primaryColor}`,
              backgroundColor: 'transparent',
              color: 'white',
              fontFamily: 'Hyperspace Race Capsule, cursive',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            CLOSE
          </button>
          <a
            href={explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            style={{
              backgroundColor: primaryColor,
              color: type === 'deposit' ? 'black' : 'black',
              fontFamily: 'Hyperspace Race Capsule, cursive',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <span>VIEW ON EXPLORER</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}
